import React, { useState, useEffect, useRef } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { MENU_ITEMS, ALL_MENU_KEYS } from '../constants/menuItems';
import { HeaderSlotProvider, useHeaderSlotRef } from '../context/HeaderSlot';

function LayoutInner() {
  const { user, rawUser, isTerminal, effectiveRole, logoutPin, logout } = useAuth();
  const navigate = useNavigate();
  const [roles, setRoles] = useState([]);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const slotRef = useHeaderSlotRef();

  useEffect(() => {
    const token = window.__cafeApiToken || localStorage.getItem('cafepos_token');
    fetch('/api/roles', { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json()).then(setRoles).catch(() => {});
  }, []);

  // Zamknij rozwijane menu po kliknięciu poza nim
  useEffect(() => {
    function handleClickOutside(e) {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  function handleLogout() {
    logout();
    navigate('/login');
  }

  const ROLE_COLORS = {
    admin:    '#A32D2D',
    manager:  '#185FA5',
    waiter:   '#3B6D11',
    kitchen:  '#854F0B',
    terminal: '#6B3FA0',
  };

  const roleColor = ROLE_COLORS[user?.role] || 'var(--text-muted)';
  const fullName = [user?.first_name, user?.last_name].filter(Boolean).join(' ') || user?.name || '—';
  const initial = (user?.first_name || user?.name || '?').charAt(0).toUpperCase();

  // Widoczność pozycji menu - admin zawsze widzi wszystko (sztywno, niezależnie od
  // konfiguracji w Ustawieniach), pozostałe role wg tego co administrator zaznaczył
  // w Ustawienia -> Widoki.
  const currentRoleConfig = roles.find(r => r.slug === effectiveRole);
  const visibleKeys = effectiveRole === 'admin' ? ALL_MENU_KEYS : (currentRoleConfig?.visible_menu_keys || []);
  const visibleItems = MENU_ITEMS.filter(item => visibleKeys.includes(item.key));

  return (
    <div className="app-layout">
      <header style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '8px 16px', background: 'var(--surface)', borderBottom: '0.5px solid var(--border)',
        position: 'relative', zIndex: 100,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          {/* Awatar - klik rozwija menu nawigacji */}
          <div ref={menuRef} style={{ position: 'relative' }}>
            <button onClick={() => setMenuOpen(o => !o)} style={{
              display: 'flex', alignItems: 'center', gap: 10, padding: '6px 10px 6px 6px',
              background: menuOpen ? 'var(--bg)' : 'none', border: 'none', borderRadius: 10, cursor: 'pointer',
            }}>
              <div style={{
                width: 32, height: 32, borderRadius: '50%',
                background: roleColor + '22', color: roleColor,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontWeight: 700, fontSize: 14, flexShrink: 0,
              }}>
                {initial}
              </div>
              <div style={{ textAlign: 'left' }}>
                <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-primary)' }}>{fullName}</div>
                <div style={{ fontSize: 11, color: roleColor }}>
                  {user?.role === 'admin' ? 'Admin' :
                   user?.role === 'manager' ? 'Manager' :
                   user?.role === 'waiter' ? 'Kelner' :
                   user?.role === 'kitchen' ? 'Kuchnia' : ''}
                </div>
              </div>
              <span style={{ fontSize: 12, color: 'var(--text-muted)', marginLeft: 2 }}>▾</span>
            </button>

            {menuOpen && (
              <div style={{
                position: 'absolute', top: '100%', left: 0, marginTop: 6, width: 230,
                background: 'var(--surface)', border: '0.5px solid var(--border-strong)', borderRadius: 12,
                boxShadow: '0 8px 24px rgba(0,0,0,0.12), 0 2px 6px rgba(0,0,0,0.08)',
                overflow: 'hidden', zIndex: 200,
              }}>
                <div style={{ padding: '6px 4px', display: 'flex', flexDirection: 'column' }}>
                  {visibleItems.map(item => (
                    item.external ? (
                      <a key={item.key} href={item.path} onClick={() => setMenuOpen(false)} style={{
                        display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px',
                        fontSize: 13, borderRadius: 8, textDecoration: 'none', color: 'var(--text-primary)',
                      }}>
                        <span style={{ width: 18, textAlign: 'center' }}>{item.icon}</span> {item.label}
                      </a>
                    ) : (
                      <NavLink key={item.key} to={item.path} end={item.end} onClick={() => setMenuOpen(false)}
                        style={({ isActive }) => ({
                          display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px',
                          fontSize: 13, borderRadius: 8, textDecoration: 'none', color: 'var(--text-primary)',
                          background: isActive ? 'var(--bg)' : 'none',
                        })}>
                        <span style={{ width: 18, textAlign: 'center' }}>{item.icon}</span> {item.label}
                      </NavLink>
                    )
                  ))}
                  {isTerminal && (
                    <NavLink to="/attendance" onClick={() => setMenuOpen(false)}
                      style={({ isActive }) => ({
                        display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px',
                        fontSize: 13, borderRadius: 8, textDecoration: 'none', color: 'var(--text-primary)',
                        background: isActive ? 'var(--bg)' : 'none',
                      })}>
                      <span style={{ width: 18, textAlign: 'center' }}>🕐</span> Czas pracy
                    </NavLink>
                  )}
                </div>
                <div style={{ borderTop: '0.5px solid var(--border)', padding: '6px 4px' }}>
                  <button onClick={handleLogout} style={{
                    display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px', width: '100%',
                    fontSize: 13, borderRadius: 8, background: 'none', border: 'none', cursor: 'pointer',
                    color: 'var(--red, #A32D2D)', textAlign: 'left',
                  }}>
                    <span style={{ width: 18, textAlign: 'center' }}>🚪</span> Wyloguj
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Zmień pracownika - zawsze widoczny, bez rozwijania menu */}
          {isTerminal && (
            <button onClick={logoutPin} style={{
              display: 'flex', alignItems: 'center', gap: 6, padding: '7px 12px',
              fontSize: 12, borderRadius: 8, border: '0.5px solid var(--border)',
              background: 'none', color: 'var(--text-muted)', cursor: 'pointer',
            }}>
              🔄 Zmień pracownika
            </button>
          )}
        </div>

        {/* Slot dla treści portalowanej ze stron (np. pigułki "3 zajęte / 38 wolne"
            z Tables.js) - flexWrap na wypadek wąskiego ekranu */}
        <div ref={slotRef} style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }} />
      </header>

      <main className="main-content">
        <Outlet />
      </main>
    </div>
  );
}

export default function Layout() {
  return (
    <HeaderSlotProvider>
      <LayoutInner />
    </HeaderSlotProvider>
  );
}
